import React, { useState } from 'react';
import { Layers, MessageSquare, Mail, CreditCard, Building2, FileText, HelpCircle, Plus, X, Move, Edit, Copy, Trash2, Save, ChevronDown, ChevronUp, Info, Layout, Menu, FilterIcon as FooterIcon } from 'lucide-react';

// Component types
export type ComponentType = 
  | 'faq' 
  | 'newsletter' 
  | 'bankComparison' 
  | 'bankList' 
  | 'loanCalculator' 
  | 'costTable' 
  | 'interestRates'
  | 'textBlock'
  | 'imageBlock'
  | 'callToAction'
  | 'header'
  | 'footer';

export interface PageComponent {
  id: string;
  type: ComponentType;
  title: string;
  order: number;
  settings: any;
  content?: any;
}

interface ComponentLibraryProps {
  onAddComponent: (componentType: ComponentType) => void;
}

const ComponentLibrary: React.FC<ComponentLibraryProps> = ({ onAddComponent }) => {
  const [activeCategory, setActiveCategory] = useState<string | null>('all');
  
  const componentCategories = [
    { id: 'all', name: 'Tüm Bileşenler', icon: <Layers size={18} /> },
    { id: 'layout', name: 'Sayfa Yapısı', icon: <Layout size={18} /> },
    { id: 'content', name: 'İçerik', icon: <FileText size={18} /> },
    { id: 'forms', name: 'Formlar', icon: <MessageSquare size={18} /> },
    { id: 'banking', name: 'Bankacılık', icon: <Building2 size={18} /> },
  ];
  
  const components = [
    { 
      id: 'header', 
      name: 'Header', 
      description: 'Sayfa üst kısmı ve navigasyon menüsü',
      icon: <Menu size={20} />,
      category: 'layout'
    },
    { 
      id: 'footer', 
      name: 'Footer', 
      description: 'Sayfa alt kısmı ve iletişim bilgileri',
      icon: <Layout size={20} />,
      category: 'layout'
    },
    { 
      id: 'faq', 
      name: 'Sık Sorulan Sorular', 
      description: 'Soru ve cevaplardan oluşan bir bölüm',
      icon: <HelpCircle size={20} />,
      category: 'content'
    },
    { 
      id: 'newsletter', 
      name: 'Bülten Kaydı', 
      description: 'E-posta bülteni kayıt formu',
      icon: <Mail size={20} />,
      category: 'forms'
    },
    { 
      id: 'bankComparison', 
      name: 'Banka Karşılaştırma', 
      description: 'Bankaları karşılaştıran bir tablo',
      icon: <Building2 size={20} />,
      category: 'banking'
    },
    { 
      id: 'bankList', 
      name: 'Banka Listesi', 
      description: 'Banka logolarını gösteren bir liste',
      icon: <Building2 size={20} />,
      category: 'banking'
    },
    { 
      id: 'loanCalculator', 
      name: 'Kredi Hesaplama', 
      description: 'Kredi hesaplama aracı',
      icon: <CreditCard size={20} />,
      category: 'banking'
    },
    { 
      id: 'costTable', 
      name: 'Maliyet Tablosu', 
      description: 'Kredi maliyetlerini gösteren tablo',
      icon: <CreditCard size={20} />,
      category: 'banking'
    },
    { 
      id: 'interestRates', 
      name: 'Faiz Oranları', 
      description: 'Bankaların faiz oranlarını gösteren tablo',
      icon: <CreditCard size={20} />,
      category: 'banking'
    },
    { 
      id: 'textBlock', 
      name: 'Metin Bloğu', 
      description: 'Özelleştirilebilir metin bloğu',
      icon: <FileText size={20} />,
      category: 'content'
    },
    { 
      id: 'imageBlock', 
      name: 'Görsel Bloğu', 
      description: 'Görsel ve açıklama içeren blok',
      icon: <FileText size={20} />,
      category: 'content'
    },
    { 
      id: 'callToAction', 
      name: 'Aksiyon Çağrısı', 
      description: 'Kullanıcıyı harekete geçiren buton ve metin',
      icon: <FileText size={20} />,
      category: 'content'
    }
  ];
  
  const filteredComponents = activeCategory === 'all' 
    ? components 
    : components.filter(comp => comp.category === activeCategory);
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="p-4 border-b border-slate-200 bg-slate-50">
        <h3 className="font-medium text-slate-800">Bileşen Kütüphanesi</h3>
      </div>
      
      {/* Categories */}
      <div className="flex border-b border-slate-200 bg-slate-50 overflow-x-auto">
        {componentCategories.map(category => (
          <button
            key={category.id}
            onClick={() => setActiveCategory(category.id)}
            className={`px-4 py-2 flex items-center gap-2 whitespace-nowrap transition-colors ${
              activeCategory === category.id
                ? 'text-[#F7BA45] border-b-2 border-[#F7BA45]'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            {category.icon}
            <span>{category.name}</span>
          </button>
        ))}
      </div>
      
      {/* Components */}
      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto">
        {filteredComponents.map(component => (
          <button
            key={component.id}
            onClick={() => onAddComponent(component.id as ComponentType)}
            className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:border-[#F7BA45] hover:bg-[#F7BA45]/5 text-left transition-all"
          >
            <div className="bg-slate-100 p-2 rounded-lg">
              {component.icon}
            </div>
            <div>
              <div className="font-medium text-slate-800">{component.name}</div>
              <div className="text-xs text-slate-500">{component.description}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default ComponentLibrary;